import { Component } from '@theme/component';

/** Fades out anyway after this long in `page_load` / `element` mode, so a slow script or a missing element can't keep the page covered. */
const MAX_WAIT_MS = 10000;

const pageLoaded = /** @type {Promise<void>} */ (
  new Promise((resolve) => {
    if (document.readyState === 'complete') resolve();
    else window.addEventListener('load', () => resolve(), { once: true });
  })
);

/**
 * Full-screen overlay with falling icons. The falling motion is pure CSS; this component only decides when to
 * fade the overlay out:
 * - `page_load`: once the window has loaded and every icon animation has finished
 * - `timer`: a fixed number of seconds after navigation start
 * - `element`: once an element matching `data-element-selector` is in the DOM and every icon animation has finished
 *
 * In the theme editor the overlay is kept in the DOM and replays when the section is selected.
 */
class FallingIconsOverlay extends Component {
  #run = 0;
  #startedAt = 0;
  #timeout = 0;
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#start();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
  }

  get #iconAnimations() {
    return [...this.querySelectorAll('.falling-icons__icon')].flatMap((icon) => icon.getAnimations());
  }

  #start() {
    const run = ++this.#run;
    const done = () => {
      if (run === this.#run) this.#hide();
    };
    const elapsed = performance.now() - this.#startedAt;
    const { endTrigger } = this.dataset;

    if (endTrigger === 'timer') {
      this.#timeout = setTimeout(done, Number(this.dataset.timer) * 1000 - elapsed);
      return;
    }

    const trigger = endTrigger === 'element' ? this.#elementAppeared() : pageLoaded;

    // `finished` rejects when an animation is cancelled (replay); the run check makes stale runs a no-op
    Promise.all([trigger, ...this.#iconAnimations.map((animation) => animation.finished)]).then(done, done);
    this.#timeout = setTimeout(done, MAX_WAIT_MS - elapsed);
  }

  /**
   * Resolves once an element matching the selector exists in the document.
   * Resolves right away when the selector is empty or invalid, so the overlay falls back to the animation end.
   * @returns {Promise<void>}
   */
  #elementAppeared() {
    const selector = this.dataset.elementSelector?.trim();
    if (!selector) return Promise.resolve();

    try {
      if (document.querySelector(selector)) return Promise.resolve();
    } catch {
      console.warn(`falling-icons-overlay: invalid element selector "${selector}"`);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.#observer = new MutationObserver(() => {
        if (!document.querySelector(selector)) return;
        this.#disconnectObserver();
        resolve();
      });
      this.#observer.observe(document.documentElement, { childList: true, subtree: true });
    });
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #cleanup() {
    clearTimeout(this.#timeout);
    this.#disconnectObserver();
  }

  #hide() {
    this.#cleanup();
    this.classList.add('is-done');

    if (window.Shopify?.designMode) return;

    this.addEventListener('transitionend', () => this.remove(), { once: true });
  }

  #handleSectionSelect = (/** @type {CustomEvent} */ event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    this.#cleanup();
    this.classList.remove('is-done');

    for (const animation of this.#iconAnimations) {
      animation.cancel();
      animation.play();
    }

    this.#startedAt = performance.now();
    this.#start();
  };
}

if (!customElements.get('falling-icons-overlay')) {
  customElements.define('falling-icons-overlay', FallingIconsOverlay);
}
